/**
 * RegenteLancamento.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Regente — Assistente de Lançamentos por IA
 *
 * INTEGRAÇÃO:
 *   import RegenteLancamento from './RegenteLancamento';
 *   <RegenteLancamento onSuccess={() => reload()} />
 *
 * O componente renderiza um FAB fixo no canto inferior direito.
 * Ao clicar, abre um painel de chat lateral com o Regente.
 *
 * REQUISITOS:
 *   - GEMINI_API_KEY no backend (usado pelas rotas /api/gastos/sugerir-conta*)
 *   - Rotas do routesFinancial.js disponíveis
 *   - React 17+
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useRef, useCallback } from "react";
import api from "../services/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    parseFloat(v) || 0
  );

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const today = () => new Date().toISOString().split("T")[0];

// ─── Regente System Prompt ────────────────────────────────────────────────────
const buildSystemPrompt = (categories = [], clients = []) => `
Você é o Regente, o assistente financeiro inteligente de um sistema ERP brasileiro.
Seu objetivo: registrar contas a pagar e a receber com fluidez, entendendo linguagem natural.

PERSONALIDADE:
- Caloroso, preciso e profissional
- Fala português do Brasil informal mas correto
- Usa emojis com elegância e parcimônia
- Nunca revela ser Claude nem da Anthropic — você é o Regente

═══════════ CAMPOS OBRIGATÓRIOS ═══════════
• Conta a Pagar:  descricao | valor | data_vencimento
• Conta a Receber: descricao | valor | data_vencimento
(Campos opcionais: cliente_nome, forma_pagamento, observacoes, parcelas)

═══════════ EXTRAÇÃO INTELIGENTE ═══════════
Extraia TUDO numa só mensagem. Exemplos:
  "Pagar R$500 de aluguel dia 10"  → type=pagar, descricao=Aluguel, valor=500, data=dia 10 próximo mês
  "Recebi 1200 da Acme semana que vem" → type=receber, descricao=Recebimento Acme, valor=1200
  "conta de luz" → pagar; "cliente pagou" → receber

DATAS — converta SEMPRE para YYYY-MM-DD:
  "dia 10" → dia 10 do mês vigente ou próximo
  "fim do mês" → último dia do mês atual
  "semana que vem" / "próxima semana" → +7 dias
  "hoje" → data de hoje
  "amanhã" → +1 dia
  "em 15 dias" → hoje + 15
  "DD/MM" ou "DD/MM/AAAA" → parse direto
  Hoje é: ${today()}

VALORES — parse flexível:
  "quinhentos", "R$500", "500,00", "5 mil", "500 reais" → todos válidos

═══════════ MODO LOTE (TRANSFORMADOR) ═══════════
Se o usuário listar vários itens separados por vírgula, ponto-e-vírgula ou quebra de linha:
  "luz 150 dia 10, internet 90 dia 15, aluguel 2000 dia 5"
→ Retorne batch=true com batch_items contendo cada item extraído.
  Para lote, não exija confirmação individual — confirme o lote inteiro.

═══════════ CAMPOS AUSENTES — 3 TENTATIVAS ═══════════
Tentativa 1 (breve):    "Qual o valor?"
Tentativa 2 (exemplo):  "Qual o valor? Ex: R$500, 1.200,00..."
Tentativa 3 (motivo):   "Preciso do valor para registrar — sem ele não consigo lançar."
Após 3 tentativas → terminate=true + oriente reiniciar

═══════════ CATEGORIAS DISPONÍVEIS ═══════════
${categories.slice(0, 25).map((c) => c.nome).join(", ") || "(nenhuma carregada)"}

═══════════ CLIENTES DISPONÍVEIS ═══════════
${clients.slice(0, 15).map((c) => c.nome).join(", ") || "(nenhum carregado)"}

═══════════ RESPOSTA — APENAS JSON VÁLIDO ═══════════
{
  "message": "mensagem amigável em pt-BR",
  "type": "pagar" | "receber" | null,
  "batch": false,
  "batch_items": [],
  "extracted": {
    "descricao": null,
    "valor": null,
    "data_vencimento": null,
    "cliente_nome": null,
    "forma_pagamento": null,
    "categoria_hint": null,
    "observacoes": null,
    "parcelas": 1
  },
  "complete": false,
  "missing": [],
  "attempt_field": null,
  "attempt_num": 0,
  "terminate": false
}
`.trim();

// ─── API Call — conversa via backend (Gemini) ────────────────────────────────
async function callRegente(history, userMessage, systemPrompt) {
  const messages = [
    ...history
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-12)
      .map((m) => ({ role: m.role, content: m.rawContent || m.content })),
    { role: "user", content: userMessage },
  ];

  const res = await api.post("/api/regente/chat", { messages, systemPrompt });

  const text = res.data?.text || "{}";
  const match = text.match(/\{[\s\S]*\}/);
  if (!match) throw new Error("Resposta inválida do modelo");
  return JSON.parse(match[0]);
}

// ─── Suggest plano de contas via backend ──────────────────────────────────────
async function suggestPlano(type, descricao, categoria_hint, valor) {
  const endpoint =
    type === "pagar"
      ? "/api/gastos/sugerir-conta-pagar"
      : "/api/gastos/sugerir-conta";
  try {
    const res = await api.post(endpoint, {
      descricao,
      categoria_nome: categoria_hint || "",
      valor: valor || 0,
      tipo_movimento: type === "pagar" ? "despesa" : "receita",
    });
    return res.data;
  } catch {
    return null;
  }
}

// ─── Submit to backend ────────────────────────────────────────────────────────
async function submitLancamento(data) {
  const endpoint =
    data.type === "pagar"
      ? "/api/contas-a-pagar/manual"
      : "/api/contas-a-receber";

  const body =
    data.type === "pagar"
      ? {
          descricao: data.descricao,
          valor: data.valor,
          data_vencimento: data.data_vencimento,
          categoria_id: data.categoria_id || null,
          plano_contas_id: data.plano_contas_id || null,
          veiculo_id: null,
          parcelas: data.parcelas || 1,
          isRecorrente: false,
        }
      : {
          descricao: data.descricao,
          valor: data.valor,
          data_vencimento: data.data_vencimento,
          cliente_nome: data.cliente_nome || null,
          forma_pagamento: data.forma_pagamento || "Boleto",
          categoria: data.categoria_hint || "Outros",
          observacoes: data.observacoes || null,
        };

  const res = await api.post(endpoint, body);
  return res.data;
}

// ─── INLINE STYLES ────────────────────────────────────────────────────────────
const css = {
  // FAB
  fab: {
    position: "fixed", bottom: "2rem", right: "2rem",
    width: 56, height: 56, borderRadius: "50%",
    background: "#0D0D14", border: "1.5px solid #F59E0B",
    color: "#F59E0B", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "1.25rem", fontFamily: "Georgia, 'Times New Roman', serif",
    fontWeight: "700", letterSpacing: "-1px",
    boxShadow: "0 0 0 4px rgba(245,158,11,0.12), 0 8px 24px rgba(0,0,0,0.4)",
    zIndex: 9998, transition: "transform 0.2s, box-shadow 0.2s",
    userSelect: "none",
  },
  // Overlay backdrop
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 9999, display: "flex", justifyContent: "flex-end",
  },
  // Drawer panel
  panel: {
    width: 420, height: "100vh",
    background: "#0D0D14",
    display: "flex", flexDirection: "column",
    borderLeft: "1px solid rgba(245,158,11,0.14)",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    animation: "slideIn 0.22s cubic-bezier(0.32,0.72,0,1)",
  },
  // Header
  header: {
    padding: "1.1rem 1.25rem",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    display: "flex", alignItems: "center", gap: "0.75rem",
    background: "#111118",
  },
  // Avatar
  avatar: {
    width: 38, height: 38, borderRadius: "50%",
    background: "rgba(245,158,11,0.1)",
    border: "1.5px solid rgba(245,158,11,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "1.1rem", color: "#F59E0B",
    fontFamily: "Georgia, serif", fontWeight: "700",
    flexShrink: 0,
  },
  // Messages container
  messages: {
    flex: 1, overflowY: "auto", padding: "1rem 1rem 0.5rem",
    display: "flex", flexDirection: "column", gap: "0.65rem",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255,255,255,0.1) transparent",
  },
  // Input bar
  inputBar: {
    padding: "0.875rem 1rem",
    borderTop: "1px solid rgba(255,255,255,0.07)",
    display: "flex", gap: "0.5rem", alignItems: "flex-end",
    background: "#111118",
  },
  // Input field
  input: {
    flex: 1, background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10, padding: "0.625rem 0.875rem",
    color: "#E2E8F0", fontSize: "0.875rem", outline: "none",
    resize: "none", lineHeight: 1.5, fontFamily: "inherit",
    maxHeight: 100, minHeight: 38,
    overflowX: "hidden", whiteSpace: "nowrap",
  },
  // Icon button
  iconBtn: (active) => ({
    width: 38, height: 38, borderRadius: 8, border: "none",
    background: active ? "rgba(245,158,11,0.18)" : "rgba(255,255,255,0.05)",
    color: active ? "#F59E0B" : "#6B7280",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", flexShrink: 0, fontSize: "1rem",
    transition: "background 0.15s, color 0.15s",
  }),
  // User bubble
  userBubble: {
    alignSelf: "flex-end", maxWidth: "80%",
    background: "#1D3461", borderRadius: "14px 14px 3px 14px",
    padding: "0.625rem 0.875rem",
    color: "#CBD5E1", fontSize: "0.875rem", lineHeight: 1.55,
  },
  // Assistant bubble
  assistantBubble: {
    alignSelf: "flex-start", maxWidth: "88%",
    background: "#1A1A27", borderRadius: "3px 14px 14px 14px",
    padding: "0.625rem 0.875rem",
    color: "#E2E8F0", fontSize: "0.875rem", lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.06)",
  },
};

// ─── keyframes via <style> tag ─────────────────────────────────────────────
const KEYFRAMES = `
@keyframes slideIn { from { transform: translateX(100%); opacity:0; } to { transform: translateX(0); opacity:1; } }
@keyframes fadeUp  { from { transform: translateY(6px); opacity:0; } to { transform: translateY(0); opacity:1; } }
@keyframes pulse   { 0%,100% { opacity:0.4; } 50% { opacity:1; } }
@keyframes spin    { to { transform: rotate(360deg); } }
@keyframes ripple  { 0% { transform:scale(1); opacity:0.8; } 100% { transform:scale(2.2); opacity:0; } }
.regente-msg { animation: fadeUp 0.2s ease-out both; }
.regente-input:focus { border-color: rgba(245,158,11,0.4) !important; box-shadow: 0 0 0 3px rgba(245,158,11,0.08); }
.regente-input::placeholder { color: #4B5563; }
.regente-btn-send:disabled { opacity:0.35; cursor:not-allowed; }
.regente-fab:hover { transform:scale(1.06); box-shadow:0 0 0 6px rgba(245,158,11,0.16),0 12px 32px rgba(0,0,0,0.5) !important; }
.regente-panel::-webkit-scrollbar { width:4px; }
.regente-panel::-webkit-scrollbar-track { background:transparent; }
.regente-panel::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.1); border-radius:4px; }
.regente-input::placeholder { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
`;

// ─── Sub-components ───────────────────────────────────────────────────────────

function TypingDots() {
  return (
    <div
      className="regente-msg"
      style={{
        ...css.assistantBubble,
        display: "flex", gap: 4, alignItems: "center", padding: "0.75rem 1rem",
      }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 7, height: 7, borderRadius: "50%",
            background: "#F59E0B", opacity: 0.4,
            animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
            display: "inline-block",
          }}
        />
      ))}
    </div>
  );
}

function PreviewCard({ data, onConfirm, onEdit, onCancel, loading }) {
  const [editing, setEditing] = useState({});
  const labels = {
    descricao: "Descrição",
    valor: "Valor",
    data_vencimento: "Vencimento",
    cliente_nome: "Cliente",
    forma_pagamento: "Forma de pagamento",
    parcelas: "Parcelas",
    plano_contas_nome: "Plano de contas",
    observacoes: "Observação",
  };

  const show = (key) =>
    data[key] !== null &&
    data[key] !== undefined &&
    data[key] !== "" &&
    !["type", "categoria_hint", "plano_contas_id", "plano_contas_codigo", "plano_confianca", "plano_justificativa", "id"].includes(key);

  const handleFieldEdit = (key, val) => {
    const updated = { ...data, [key]: key === "valor" ? parseFloat(val) || 0 : val };
    setEditing({});
    onEdit(updated);
  };

  const confiancaColor = {
    alta: "#10B981", media: "#F59E0B", baixa: "#EF4444"
  };

  return (
    <div
      className="regente-msg"
      style={{
        alignSelf: "flex-start", width: "calc(100% - 0px)",
        background: "#16162A",
        border: "1px solid rgba(245,158,11,0.25)",
        borderRadius: 14, padding: "1rem",
        fontSize: "0.875rem",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.875rem" }}>
        <span style={{
          background: data.type === "pagar" ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.12)",
          color: data.type === "pagar" ? "#F87171" : "#34D399",
          padding: "3px 10px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 600,
          border: `1px solid ${data.type === "pagar" ? "rgba(239,68,68,0.25)" : "rgba(16,185,129,0.25)"}`,
        }}>
          {data.type === "pagar" ? "▼ CONTA A PAGAR" : "▲ CONTA A RECEBER"}
        </span>
        <button
          onClick={() => onEdit(null)}
          style={{
            marginLeft: "auto", background: "none", border: "none",
            color: "#6B7280", cursor: "pointer", fontSize: "0.75rem",
            padding: "2px 6px", borderRadius: 4,
          }}
          title="Editar campos"
        >
          ✏️ editar
        </button>
      </div>

      {/* Fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {Object.keys(labels).map((key) => {
          if (!show(key)) return null;
          let displayVal = data[key];
          if (key === "valor") displayVal = fmtBRL(data[key]);
          if (key === "data_vencimento") displayVal = fmtDate(data[key]);
          if (key === "parcelas" && (!data[key] || data[key] === 1)) return null;

          return (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#6B7280", width: 130, flexShrink: 0, fontSize: "0.8rem" }}>
                {labels[key]}
              </span>
              {editing[key] ? (
                <input
                  autoFocus
                  defaultValue={data[key]}
                  onBlur={(e) => handleFieldEdit(key, e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleFieldEdit(key, e.target.value)}
                  style={{
                    flex: 1, background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(245,158,11,0.4)",
                    borderRadius: 6, padding: "3px 8px",
                    color: "#E2E8F0", fontSize: "0.85rem", outline: "none",
                  }}
                />
              ) : (
                <span
                  style={{ color: "#E2E8F0", fontWeight: 500, flex: 1, cursor: "text" }}
                  onClick={() => setEditing({ [key]: true })}
                  title="Clique para editar"
                >
                  {String(displayVal)}
                </span>
              )}
            </div>
          );
        })}

        {/* Plano de contas badge */}
        {data.plano_contas_nome && (
          <div style={{ marginTop: 4 }}>
            <span style={{ color: "#6B7280", fontSize: "0.8rem" }}>Plano de contas</span>
            <div style={{
              marginTop: 4, background: "rgba(245,158,11,0.07)",
              border: "1px solid rgba(245,158,11,0.2)",
              borderRadius: 8, padding: "6px 10px",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ color: "#F59E0B", fontSize: "0.8rem", fontWeight: 600 }}>
                {data.plano_contas_codigo}
              </span>
              <span style={{ color: "#D1D5DB", fontSize: "0.8rem", flex: 1 }}>
                {data.plano_contas_nome}
              </span>
              {data.plano_confianca && (
                <span style={{
                  fontSize: "0.7rem", color: confiancaColor[data.plano_confianca] || "#6B7280",
                  fontWeight: 600,
                }}>
                  {data.plano_confianca === "alta" ? "✓ alta" : data.plano_confianca === "media" ? "~ média" : "! baixa"}
                </span>
              )}
            </div>
            {data.plano_justificativa && (
              <p style={{ margin: "4px 0 0", fontSize: "0.72rem", color: "#6B7280", fontStyle: "italic" }}>
                "{data.plano_justificativa}"
              </p>
            )}
          </div>
        )}
      </div>

      {/* Total highlight */}
      <div style={{
        marginTop: "0.875rem", padding: "0.75rem",
        background: "rgba(255,255,255,0.03)",
        borderRadius: 10, textAlign: "center",
      }}>
        <span style={{ color: "#6B7280", fontSize: "0.75rem", display: "block", marginBottom: 2 }}>
          TOTAL A {data.type === "pagar" ? "PAGAR" : "RECEBER"}
        </span>
        <span style={{
          color: data.type === "pagar" ? "#F87171" : "#34D399",
          fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.5px",
        }}>
          {fmtBRL(data.valor)}
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, marginTop: "0.875rem" }}>
        <button
          onClick={onCancel}
          disabled={loading}
          style={{
            flex: 1, padding: "0.6rem",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 10, color: "#9CA3AF",
            cursor: "pointer", fontSize: "0.85rem", fontWeight: 500,
          }}
        >
          Corrigir
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          style={{
            flex: 2, padding: "0.6rem",
            background: loading ? "rgba(245,158,11,0.3)" : "#F59E0B",
            border: "none", borderRadius: 10,
            color: loading ? "rgba(0,0,0,0.5)" : "#000",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: "0.85rem", fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}
        >
          {loading ? (
            <>
              <span style={{ width: 14, height: 14, border: "2px solid rgba(0,0,0,0.3)", borderTopColor: "#000", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
              Registrando...
            </>
          ) : (
            "✓ Confirmar lançamento"
          )}
        </button>
      </div>
    </div>
  );
}

function BatchPreviewCard({ items, onConfirm, onCancel, loading }) {
  const total = items.reduce((a, c) => a + (parseFloat(c.valor) || 0), 0);
  return (
    <div
      className="regente-msg"
      style={{
        alignSelf: "flex-start", width: "100%",
        background: "#16162A",
        border: "1px solid rgba(245,158,11,0.25)",
        borderRadius: 14, padding: "1rem",
        fontSize: "0.875rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.875rem" }}>
        <span style={{
          background: "rgba(245,158,11,0.12)", color: "#F59E0B",
          padding: "3px 10px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 600,
          border: "1px solid rgba(245,158,11,0.25)",
        }}>
          📦 LOTE — {items.length} LANÇAMENTOS
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
        {items.map((item, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "6px 8px", background: "rgba(255,255,255,0.03)",
            borderRadius: 8,
          }}>
            <span style={{
              width: 20, height: 20, borderRadius: "50%",
              background: item.type === "pagar" ? "rgba(239,68,68,0.15)" : "rgba(16,185,129,0.15)",
              color: item.type === "pagar" ? "#F87171" : "#34D399",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.65rem", fontWeight: 700, flexShrink: 0,
            }}>
              {item.type === "pagar" ? "▼" : "▲"}
            </span>
            <span style={{ flex: 1, color: "#D1D5DB", fontSize: "0.82rem" }}>{item.descricao}</span>
            <span style={{ color: "#E2E8F0", fontWeight: 600, fontSize: "0.82rem" }}>{fmtBRL(item.valor)}</span>
            <span style={{ color: "#6B7280", fontSize: "0.75rem" }}>{fmtDate(item.data_vencimento)}</span>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: "0.75rem", display: "flex", justifyContent: "space-between",
        padding: "8px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 8,
      }}>
        <span style={{ color: "#6B7280", fontSize: "0.8rem" }}>Total do lote</span>
        <span style={{ color: "#F59E0B", fontWeight: 700 }}>{fmtBRL(total)}</span>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: "0.875rem" }}>
        <button
          onClick={onCancel} disabled={loading}
          style={{
            flex: 1, padding: "0.6rem",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 10, color: "#9CA3AF",
            cursor: "pointer", fontSize: "0.85rem",
          }}
        >
          Cancelar
        </button>
        <button
          onClick={onConfirm} disabled={loading}
          style={{
            flex: 2, padding: "0.6rem",
            background: loading ? "rgba(245,158,11,0.3)" : "#F59E0B",
            border: "none", borderRadius: 10,
            color: loading ? "rgba(0,0,0,0.5)" : "#000",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: "0.85rem", fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}
        >
          {loading ? (
            <>
              <span style={{ width: 14, height: 14, border: "2px solid rgba(0,0,0,0.3)", borderTopColor: "#000", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
              Registrando {items.length} lançamentos...
            </>
          ) : (
            `✓ Registrar ${items.length} lançamentos`
          )}
        </button>
      </div>
    </div>
  );
}

function TemplatesPanel({ templates, onSelect, onClose, onClear }) {
  if (!templates.length) return null;
  return (
    <div style={{
      position: "absolute", top: "100%", right: 0,
      width: 300, background: "#16162A",
      border: "1px solid rgba(245,158,11,0.2)",
      borderRadius: 12, padding: "0.75rem",
      zIndex: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
    }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <span style={{ color: "#9CA3AF", fontSize: "0.75rem", fontWeight: 600, flex: 1 }}>
          LANÇAMENTOS FREQUENTES
        </span>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "#6B7280", cursor: "pointer", fontSize: "1rem" }}>✕</button>
      </div>
      {templates.map((t) => (
        <button
          key={t.id}
          onClick={() => onSelect(t)}
          style={{
            width: "100%", padding: "8px 10px", marginBottom: 4,
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 8, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 8, textAlign: "left",
          }}
        >
          <span style={{
            width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
            background: t.type === "pagar" ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.12)",
            color: t.type === "pagar" ? "#F87171" : "#34D399",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.6rem",
          }}>
            {t.type === "pagar" ? "▼" : "▲"}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ color: "#D1D5DB", fontSize: "0.82rem", fontWeight: 500 }}>{t.descricao}</div>
            <div style={{ color: "#6B7280", fontSize: "0.72rem" }}>{fmtBRL(t.valor)} · {t.count || 1}× usado</div>
          </div>
        </button>
      ))}
      <button
        onClick={onClear}
        style={{
          width: "100%", marginTop: 4, padding: "6px",
          background: "none", border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 6, color: "#4B5563", cursor: "pointer", fontSize: "0.72rem",
        }}
      >
        Limpar histórico
      </button>
    </div>
  );
}

// ─── Markdown-lite renderer ───────────────────────────────────────────────────
function MsgText({ text }) {
  const rendered = text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\n/g, "<br/>");
  return <span dangerouslySetInnerHTML={{ __html: rendered }} />;
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
// inline=true → renderiza botão no header em vez do FAB fixo
export default function RegenteLancamento({ onSuccess, inline = false }) {
  const [open, setOpen]               = useState(false);
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState("");
  const [loading, setLoading]         = useState(false);
  const [stage, setStage]             = useState("idle");
  // "idle" | "collecting" | "suggesting" | "preview" | "batch_preview" | "submitting" | "success" | "failed"

  const [collected, setCollected]     = useState(null);
  const [batchItems, setBatchItems]   = useState([]);
  const [attempts, setAttempts]       = useState({});
  const [categories, setCategories]   = useState([]);
  const [clients, setClients]         = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [recording, setRecording]     = useState(false);
  const [templates, setTemplates]     = useState(() => {
    try { return JSON.parse(localStorage.getItem("regente_templates") || "[]"); }
    catch { return []; }
  });

  const msgEndRef    = useRef(null);
  const inputRef     = useRef(null);
  const recognRef    = useRef(null);

  // ── Scroll to bottom ───────────────────────────────────────────────────────
  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // ── Load data on open ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    Promise.allSettled([
      api.get("/api/gastos/categorias"),
      api.get("/api/clientes/select"),
    ]).then(([cats, cls]) => {
      if (cats.status === "fulfilled" && Array.isArray(cats.value?.data)) setCategories(cats.value.data);
      if (cls.status  === "fulfilled" && Array.isArray(cls.value?.data))  setClients(cls.value.data);
    });
    if (messages.length === 0) greetUser();
  }, [open]);

  const greetUser = () => {
    const hour = new Date().getHours();
    const saudacao =
      hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";

    setMessages([
      {
        role: "assistant",
        content: `${saudacao}! Sou o **Regente** 🎼 — aqui para fazer seus lançamentos financeiros com velocidade e precisão.\n\nMe diga o que precisa em linguagem natural. Pode mandar tudo de uma vez, inclusive **vários lançamentos** separados por vírgula!`,
        ts: Date.now(),
      },
    ]);
  };

  // ── Add message ────────────────────────────────────────────────────────────
  const addMsg = useCallback((role, content, meta = {}) => {
    setMessages((prev) => [...prev, { role, content, ts: Date.now(), rawContent: content, ...meta }]);
  }, []);

  // ── Voice input ────────────────────────────────────────────────────────────
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      addMsg("assistant", "😅 Seu navegador não suporta voz — mas pode digitar normalmente!");
      return;
    }
    if (recording) {
      recognRef.current?.stop();
      setRecording(false);
      return;
    }
    const r = new SR();
    r.lang = "pt-BR";
    r.continuous = false;
    r.interimResults = false;
    r.onresult = (e) => {
      setInput(e.results[0][0].transcript);
      setRecording(false);
    };
    r.onerror = r.onend = () => setRecording(false);
    recognRef.current = r;
    r.start();
    setRecording(true);
  };

  // ── Handle send ────────────────────────────────────────────────────────────
  const handleSend = useCallback(async (msg = input) => {
    const text = String(msg || "").trim();
    if (!text || loading) return;
    setInput("");
    addMsg("user", text);
    setLoading(true);

    try {
      const systemPrompt = buildSystemPrompt(categories, clients);

      // Build context string for the AI
      const contextStr =
        collected || Object.keys(attempts).length
          ? `\n\n[CONTEXTO ATUAL]\nColetado: ${JSON.stringify(collected || {})}\nTentativas: ${JSON.stringify(attempts)}`
          : "";

      const result = await callRegente(messages, text + contextStr, systemPrompt);

      // ── Termination ────────────────────────────────────────────────────────
      if (result.terminate) {
        addMsg("assistant", result.message, { isTerminated: true });
        setStage("failed");
        setLoading(false);
        return;
      }

      // Add assistant reply
      addMsg("assistant", result.message);

      // ── Merge extracted data ───────────────────────────────────────────────
      const newData = {
        ...(collected || {}),
        type: result.type || collected?.type || null,
        ...Object.fromEntries(
          Object.entries(result.extracted || {}).filter(([, v]) => v !== null && v !== undefined)
        ),
      };
      setCollected(newData);

      // ── Update attempt tracking ────────────────────────────────────────────
      if (result.attempt_field && result.attempt_num) {
        setAttempts((prev) => ({ ...prev, [result.attempt_field]: result.attempt_num }));
      }

      // ── Batch mode ─────────────────────────────────────────────────────────
      if (result.batch && Array.isArray(result.batch_items) && result.batch_items.length > 0) {
        setBatchItems(result.batch_items);
        setStage("batch_preview");
        setLoading(false);
        return;
      }

      // ── Ready to preview ───────────────────────────────────────────────────
      if (result.complete || result.ready_to_confirm) {
        setStage("suggesting");

        // Fetch plano de contas from backend
        const plano = await suggestPlano(
          newData.type,
          newData.descricao,
          newData.categoria_hint,
          newData.valor
        );

        const finalData = {
          ...newData,
          plano_contas_id: plano?.id || null,
          plano_contas_nome: plano?.nome || null,
          plano_contas_codigo: plano?.codigo || null,
          plano_confianca: plano?.confianca || null,
          plano_justificativa: plano?.justificativa || null,
        };

        setCollected(finalData);
        setStage("preview");
      } else {
        setStage("collecting");
      }
    } catch (err) {
      console.error("[Regente]", err);
      addMsg("assistant", "Ops, tive um soluço técnico 🔄 Pode tentar de novo?");
      setStage("collecting");
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, collected, attempts, categories, clients, addMsg]);

  // ── Confirm single ─────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!collected) return;
    setStage("submitting");
    setLoading(true);

    try {
      await submitLancamento(collected);
      saveTemplate(collected);
      setStage("success");
      addMsg(
        "assistant",
        `🎉 Lançamento registrado com sucesso!\n\n**${collected.descricao}** · ${fmtBRL(collected.valor)} · vence ${fmtDate(collected.data_vencimento)}\n\nQuer lançar mais alguma coisa? Pode mandar!`,
        { isSuccess: true }
      );
      onSuccess?.();
      setCollected(null);
      setAttempts({});
    } catch (err) {
      addMsg("assistant", `❌ ${err.message}\n\nPosso tentar corrigir — o que quer ajustar?`);
      setStage("preview");
    } finally {
      setLoading(false);
    }
  };

  // ── Confirm batch ──────────────────────────────────────────────────────────
  const handleConfirmBatch = async () => {
    if (!batchItems.length) return;
    setStage("submitting");
    setLoading(true);
    let successCount = 0;
    const errors = [];

    for (const item of batchItems) {
      try {
        await submitLancamento(item);
        saveTemplate(item);
        successCount++;
      } catch (e) {
        errors.push(`${item.descricao}: ${e.message}`);
      }
    }

    const msg =
      errors.length === 0
        ? `🎉 **${successCount} lançamentos** registrados com sucesso!\n\nTotal: ${fmtBRL(batchItems.reduce((a, c) => a + parseFloat(c.valor || 0), 0))}\n\nPode mandar mais!`
        : `✅ ${successCount} registrados com sucesso.\n❌ Falhas:\n${errors.join("\n")}`;

    addMsg("assistant", msg, { isSuccess: errors.length === 0 });
    setBatchItems([]);
    setStage("success");
    setLoading(false);
    if (errors.length === 0) onSuccess?.();
  };

  // ── Save template ──────────────────────────────────────────────────────────
  const saveTemplate = (data) => {
    if (!data?.descricao) return;
    setTemplates((prev) => {
      const existing = prev.findIndex(
        (t) => t.descricao?.toLowerCase() === data.descricao?.toLowerCase()
      );
      const entry = { ...data, id: Date.now(), count: 1 };
      let updated;
      if (existing >= 0) {
        updated = prev.map((t, i) =>
          i === existing ? { ...t, count: (t.count || 1) + 1 } : t
        );
      } else {
        updated = [entry, ...prev].slice(0, 12);
      }
      localStorage.setItem("regente_templates", JSON.stringify(updated));
      return updated;
    });
  };

  // ── Reset flow ─────────────────────────────────────────────────────────────
  const handleReset = () => {
    setStage("idle");
    setCollected(null);
    setBatchItems([]);
    setAttempts({});
    setMessages([]);
    setTimeout(greetUser, 50);
  };

  // ── Template quick-fill ────────────────────────────────────────────────────
  const handleTemplateSelect = (t) => {
    setShowTemplates(false);
    const msg = `Lançar ${t.type === "pagar" ? "conta a pagar" : "conta a receber"}: ${t.descricao}, valor R$${t.valor}, vencimento próximo mês mesmo dia`;
    handleSend(msg);
  };

  const clearTemplates = () => {
    setTemplates([]);
    localStorage.removeItem("regente_templates");
    setShowTemplates(false);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{KEYFRAMES}</style>

      {/* ── Trigger: inline (header) ou FAB fixo ── */}
      {!open && (inline ? (
        <button
          onClick={() => setOpen(true)}
          title="Regente IA — Lançamento rápido"
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "0 16px", height: 38,
            background: "rgba(245,158,11,0.08)",
            border: "1.5px solid rgba(245,158,11,0.45)",
            borderRadius: 8, cursor: "pointer",
            color: "#F59E0B", fontWeight: 700,
            fontSize: "0.85rem", fontFamily: "inherit",
            transition: "background 0.15s, border-color 0.15s",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(245,158,11,0.16)";
            e.currentTarget.style.borderColor = "rgba(245,158,11,0.8)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(245,158,11,0.08)";
            e.currentTarget.style.borderColor = "rgba(245,158,11,0.45)";
          }}
        >
          <span style={{
            width: 20, height: 20, borderRadius: "50%",
            background: "rgba(245,158,11,0.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.8rem", fontFamily: "Georgia, serif",
          }}>R</span>
          Regente IA
        </button>
      ) : (
        <button
          className="regente-fab"
          onClick={() => setOpen(true)}
          title="Regente — Lançamentos por IA"
          style={css.fab}
        >
          R
        </button>
      ))}

      {/* ── Overlay + Drawer ── */}
      {open && (
        <div style={css.overlay} onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div style={css.panel} className="regente-panel">

            {/* HEADER */}
            <div style={css.header}>
              <div style={css.avatar}>R</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#F59E0B", fontSize: "0.92rem", fontWeight: 700, letterSpacing: "-0.3px" }}>
                  Regente
                </div>
                <div style={{ color: "#4B5563", fontSize: "0.72rem", marginTop: 1 }}>
                  {stage === "suggesting"
                    ? "🎯 Buscando classificação contábil..."
                    : stage === "submitting"
                    ? "⏳ Registrando..."
                    : "Assistente de lançamentos · IA"}
                </div>
              </div>

              {/* Templates button */}
              <div style={{ position: "relative" }}>
                {templates.length > 0 && (
                  <button
                    onClick={() => setShowTemplates((v) => !v)}
                    style={css.iconBtn(showTemplates)}
                    title="Lançamentos frequentes"
                  >
                    🗂
                  </button>
                )}
                {showTemplates && (
                  <TemplatesPanel
                    templates={templates}
                    onSelect={handleTemplateSelect}
                    onClose={() => setShowTemplates(false)}
                    onClear={clearTemplates}
                  />
                )}
              </div>

              {/* Close */}
              <button
                onClick={() => setOpen(false)}
                style={{ ...css.iconBtn(false), marginLeft: 2 }}
                title="Fechar"
              >
                ✕
              </button>
            </div>

            {/* MESSAGES */}
            <div style={css.messages}>
              {messages.map((msg, idx) => {
                if (msg.isPreview) return null; // rendered via stage

                if (msg.role === "user") {
                  return (
                    <div key={idx} className="regente-msg" style={css.userBubble}>
                      <MsgText text={msg.content} />
                    </div>
                  );
                }

                return (
                  <div key={idx} className="regente-msg">
                    {/* Success badge */}
                    {msg.isSuccess && (
                      <div style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: "rgba(16,185,129,0.1)",
                        border: "1px solid rgba(16,185,129,0.25)",
                        borderRadius: 8, padding: "4px 10px",
                        marginBottom: 6, fontSize: "0.75rem", color: "#34D399", fontWeight: 600,
                      }}>
                        ✓ LANÇAMENTO CONFIRMADO
                      </div>
                    )}
                    <div style={css.assistantBubble}>
                      <MsgText text={msg.content} />
                    </div>
                    {/* Reset button on termination */}
                    {msg.isTerminated && (
                      <button
                        onClick={handleReset}
                        style={{
                          marginTop: 8, padding: "6px 14px",
                          background: "rgba(245,158,11,0.12)",
                          border: "1px solid rgba(245,158,11,0.3)",
                          borderRadius: 8, color: "#F59E0B",
                          cursor: "pointer", fontSize: "0.8rem", fontWeight: 600,
                        }}
                      >
                        🔄 Reiniciar conversa
                      </button>
                    )}
                    {/* New flow after success */}
                    {msg.isSuccess && stage === "success" && (
                      <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                        <button
                          onClick={handleReset}
                          style={{
                            padding: "5px 12px",
                            background: "rgba(245,158,11,0.1)",
                            border: "1px solid rgba(245,158,11,0.25)",
                            borderRadius: 7, color: "#F59E0B",
                            cursor: "pointer", fontSize: "0.78rem",
                          }}
                        >
                          + Novo lançamento
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Loading dots */}
              {loading && stage !== "submitting" && <TypingDots />}

              {/* Preview card */}
              {stage === "preview" && collected && (
                <PreviewCard
                  data={collected}
                  onConfirm={handleConfirm}
                  onEdit={(upd) => { setCollected(upd); if (!upd) addMsg("assistant", "Ok! O que quer corrigir?"); }}
                  onCancel={() => { addMsg("assistant", "Claro! Me diga o que quer alterar 🖊️"); setStage("collecting"); }}
                  loading={stage === "submitting" || loading}
                />
              )}

              {/* Batch preview */}
              {stage === "batch_preview" && batchItems.length > 0 && (
                <BatchPreviewCard
                  items={batchItems}
                  onConfirm={handleConfirmBatch}
                  onCancel={() => { setBatchItems([]); setStage("idle"); addMsg("assistant", "Lote cancelado. O que quer lançar?"); }}
                  loading={stage === "submitting" || loading}
                />
              )}

              {/* Suggesting spinner */}
              {stage === "suggesting" && (
                <div className="regente-msg" style={{
                  ...css.assistantBubble,
                  display: "flex", alignItems: "center", gap: 10,
                  color: "#9CA3AF", fontSize: "0.82rem",
                }}>
                  <span style={{
                    width: 14, height: 14,
                    border: "2px solid rgba(245,158,11,0.2)", borderTopColor: "#F59E0B",
                    borderRadius: "50%", animation: "spin 0.7s linear infinite",
                    display: "inline-block", flexShrink: 0,
                  }} />
                  Consultando plano de contas via IA...
                </div>
              )}

              <div ref={msgEndRef} />
            </div>

            {/* INPUT BAR */}
            <div style={css.inputBar}>
              {/* Voice button */}
              <button
                onClick={toggleVoice}
                style={{
                  ...css.iconBtn(recording),
                  position: "relative", overflow: "hidden",
                }}
                title={recording ? "Parar gravação" : "Entrada por voz"}
              >
                {recording ? "⏹" : "🎤"}
                {recording && (
                  <span style={{
                    position: "absolute", inset: 0, borderRadius: "50%",
                    border: "2px solid #F59E0B",
                    animation: "ripple 1s ease-out infinite",
                  }} />
                )}
              </button>

              {/* Text input */}
              <textarea
                ref={inputRef}
                className="regente-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={
                  stage === "preview"
                    ? "Diga o que quer corrigir..."
                    : recording
                    ? "Ouvindo..."
                    : "Digite ou fale: 'Pagar 500 de aluguel dia 10'..."
                }
                disabled={loading || stage === "submitting"}
                rows={1}
                style={css.input}
              />

              {/* Send button */}
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="regente-btn-send"
                style={{
                  ...css.iconBtn(false),
                  background: input.trim() && !loading
                    ? "rgba(245,158,11,0.15)" : "rgba(255,255,255,0.04)",
                  color: input.trim() && !loading ? "#F59E0B" : "#374151",
                  border: "1px solid " + (input.trim() && !loading
                    ? "rgba(245,158,11,0.3)" : "rgba(255,255,255,0.07)"),
                  transition: "all 0.15s",
                }}
                title="Enviar (Enter)"
              >
                ➤
              </button>
            </div>

            {/* Hint strip */}
            <div style={{
              padding: "6px 1rem 10px",
              background: "#111118",
              display: "flex", gap: 8, flexWrap: "wrap",
            }}>
              {[
                "Pagar conta de luz",
                "Receita de cliente",
                "Lote: luz + internet + aluguel",
              ].map((hint) => (
                <button
                  key={hint}
                  onClick={() => handleSend(hint)}
                  disabled={loading}
                  style={{
                    padding: "3px 9px",
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 20, color: "#6B7280",
                    cursor: "pointer", fontSize: "0.72rem",
                    transition: "border-color 0.15s, color 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(245,158,11,0.3)";
                    e.currentTarget.style.color = "#9CA3AF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.color = "#6B7280";
                  }}
                >
                  {hint}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}